/**
 * Circles - Social Groups Feature
 * Topic-based groups for youth to connect and discuss
 * Copying Discord channels + Slack communities
 */

import React, { useState, useEffect } from 'react';
import { Users, Send, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import Navigation from '@/components/Navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CIRCLE_TOPICS = [
  { id: 'career_talk', name: 'Career Talk', emoji: '💼' },
  { id: 'tech', name: 'Tech & Code', emoji: '💻' },
  { id: 'creative', name: 'Creative Corner', emoji: '🎨' },
  { id: 'music', name: 'Music & Arts', emoji: '🎵' },
  { id: 'success', name: 'Success Stories', emoji: '🏆' },
  { id: 'general', name: 'General Chat', emoji: '💬' }
];

const CirclesPage = ({ user }) => {
  const { colors } = useTheme();
  const [selectedCircle, setSelectedCircle] = useState(CIRCLE_TOPICS[0]);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');

  useEffect(() => {
    fetchPosts(selectedCircle.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCircle]);

  const fetchPosts = async (circleId) => {
    try {
      const res = await fetch(`${API_URL}/api/circles/${circleId}/posts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const createPost = async () => {
    if (!newPost.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/circles/${selectedCircle.id}/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content: newPost })
      });
      if (res.ok) {
        setNewPost('');
        fetchPosts(selectedCircle.id);
        toast.success('Posted! 💬');
      }
    } catch (error) {
      toast.error('Error posting');
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: colors.background }}>
      <div className="p-6 rounded-b-3xl shadow-lg mb-6" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold text-white">Circles 👥</h1>
          <p className="text-white/90">Connect with peers!</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-12 gap-6">
        <div className="md:col-span-4">
          <Card className="p-4" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <h3 className="font-bold mb-4" style={{ color: colors.text }}>Circles</h3>
            {CIRCLE_TOPICS.map(topic => (
              <Card
                key={topic.id}
                className={`p-3 mb-2 cursor-pointer ${selectedCircle?.id === topic.id ? 'ring-2 ring-blue-500' : ''}`}
                style={{ backgroundColor: colors.surface, borderColor: colors.border }}
                onClick={() => setSelectedCircle(topic)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{topic.emoji}</span>
                  <p className="font-semibold text-sm" style={{ color: colors.text }}>{topic.name}</p>
                </div>
              </Card>
            ))}
          </Card>
        </div>

        <div className="md:col-span-8">
          <Card className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: colors.text }}>{selectedCircle.emoji} {selectedCircle.name}</h2>
            
            <Textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder="Share your thoughts..."
              rows={3}
              className="mb-2"
              style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
            />
            <Button onClick={createPost} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold mb-6">
              <Send className="w-4 h-4 mr-2" />Post
            </Button>

            <div className="space-y-3">
              {posts.length === 0 ? (
                <p className="text-center py-8" style={{ color: colors.text_secondary }}>No posts yet. Start the conversation!</p>
              ) : (
                posts.map(post => (
                  <Card key={post.id} className="p-4" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
                    <div className="flex gap-3">
                      <span className="text-2xl">{post.user_avatar}</span>
                      <div className="flex-1">
                        <p className="font-bold text-sm" style={{ color: colors.text }}>{post.user_name}</p>
                        <p className="text-sm mt-1" style={{ color: colors.text }}>{post.content}</p>
                        <p className="text-xs mt-2" style={{ color: colors.text_secondary }}>{new Date(post.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>

      <Navigation user={user} />
    </div>
  );
};

export default CirclesPage;
